import type {
  AppDocsQuery,
  RecoverInvoiceMutationVariables,
  RequestPaymentMutationVariables,
  RequestReviewMutationVariables,
  SearchQuotesQuery,
  SendQuoteMutationVariables
} from '@paintscout/api'
import {
  useAcceptAdditionalWorkMutation,
  useAcceptQuoteMutation,
  useRequestPaymentMutation,
  useSyncQuoteMutation,
  useUnlinkDealMutation,
  useRequestReviewMutation,
  useRecoverInvoiceMutation,
  ActivitiesForDocDocument,
  AppDocsDocument,
  SearchQuotesDocument,
  UnscheduledJobsDocument,
  evictQueries,
  updateCachedQuote,
  updateCachedQuotePartially,
  updateQueries,
  useArchiveQuotesMutation,
  useDeleteQuotesMutation,
  useSaveQuoteMutation,
  useSendQuoteMutation,
  useLinkDealMutation
} from '@paintscout/api'
import { useUser } from '@paintscout/ui-business'
import { TRIAL_QUOTE_LIMIT } from '@paintscout/util'
import type ApolloCache from '@wora/apollo-cache'
import { getOperationName } from 'apollo-link'
import type { QuoteDocument } from 'paintscout'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { track } from '../util/analytics'
import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import { QUOTE_SIDEBAR_INFO_QUERY_NAME } from '../graphql/util/queryNames'
import { filterSearchQuotesByIds, isQuoteRestoreTransition, readCachedQuoteFlags } from './quoteCacheHelpers'

/**
 * Returns common useMutations for saving quotes and handles updating the caches
 */
export function useQuoteMutations() {
  const { isTrial } = useUser()
  const [saveQuoteMutation] = useSaveQuoteMutation()
  const [deleteQuotesMutation] = useDeleteQuotesMutation()
  const [archiveQuotesMutation] = useArchiveQuotesMutation()
  const [sendQuoteMutation] = useSendQuoteMutation()
  const [requestReviewMutation] = useRequestReviewMutation()
  const [requestPaymentMutation] = useRequestPaymentMutation()
  const [recoverInvoiceMutation] = useRecoverInvoiceMutation()
  const [syncQuoteMutation] = useSyncQuoteMutation()
  const [unlinkDealMutation] = useUnlinkDealMutation()
  const [linkDealMutation] = useLinkDealMutation()
  const [acceptQuoteMutation] = useAcceptQuoteMutation()
  const [acceptAdditionalWorkMutation] = useAcceptAdditionalWorkMutation()

  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveQuote({
        quote,
        force,
        autosave
      }: {
        quote: QuoteDocument
        force?: boolean
        // True when this save is a side effect of a state update (e.g. confirming a dialog),
        // not a dedicated user "Save" action. Tells the server to skip the translation
        // pre-bake, which otherwise re-translates on every such save.
        autosave?: boolean
      }) {
        const timeOfSave = Date.now()

        // if mutation is queued, this will be the id so we can combine multiple quote saves later
        const mutationId = `save-quote-${quote._id}`

        const quoteResult = await saveQuoteMutation({
          variables: {
            quote,
            force,
            autosave
          },
          context: {
            __mutation_id__: mutationId
          },
          optimisticResponse: {
            saveQuote: makeQuoteOptimistic(quote)
          },
          // QuoteSidebar's `sidebarInfo` query is deliberately NOT refetched here: save is a hot
          // path that's already been tuned for cost (#21655), and the sidebar refreshes its
          // last-activity line on its own 50s poll. (This slot used to name 'getLastActivity',
          // an operation that hasn't existed since the sidebar query was renamed — see #22137.)
          refetchQueries: [getOperationName(ActivitiesForDocDocument), getOperationName(SearchQuotesDocument)],
          update(cache: ApolloCache, { data }) {
            const savedQuote = makeQuoteOptimistic(data?.saveQuote)

            if (savedQuote) {
              // Detect a restore transition: the cached quote was trashed (or had a deleteDate),
              // and the new save clears those flags. RecoveryNotice additionally sets `isRestored`
              // on the quote as an explicit signal. Eviction ensures list views mounted AFTER the
              // restore don't serve stale cache-first data that omits the restored quote;
              // the `SearchQuotesDocument` entry in `refetchQueries` above covers the
              // currently-mounted case.
              const prior = readCachedQuoteFlags(cache, savedQuote._id)
              const shouldEvictSearchQuotes = isQuoteRestoreTransition({
                priorTrashed: prior?.trashed,
                priorDeleteDate: prior?.deleteDate,
                nextTrashed: savedQuote.trashed,
                nextDeleted: savedQuote._deleted,
                isRestored: quote.isRestored
              })

              updateCachedQuote(cache, savedQuote)

              if (shouldEvictSearchQuotes) {
                evictQueries(cache, SearchQuotesDocument)
              }
            }

            // update trial remainingQuotes
            if (!quote._rev && isTrial) {
              const appDocsQuery = cache.readQuery<AppDocsQuery>({ query: AppDocsDocument })

              if (appDocsQuery.appDocs.remainingQuotes) {
                cache.writeQuery<AppDocsQuery>({
                  query: AppDocsDocument,
                  data: {
                    ...appDocsQuery,
                    appDocs: {
                      ...appDocsQuery.appDocs,
                      // Don't let remainingQuotes go below 0
                      remainingQuotes: Math.max(appDocsQuery.appDocs.remainingQuotes - 1, 0)
                    }
                  }
                })
              }
            }
          }
        })

        // remove any previous offline saveQuote mutations for this quote
        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        return {
          quote: quoteResult.data.saveQuote
        }
      },

      async deleteQuotes(quoteIds: string[], softDelete?: boolean) {
        track('Quote(s) Deleted', { quoteIds })
        return deleteQuotesMutation({
          variables: { ids: quoteIds, softDelete },
          optimisticResponse: {
            deleteQuotes: quoteIds.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null,
              reason: null
            }))
          },
          refetchQueries: [
            getOperationName(SearchQuotesDocument) // update remainingQuotes
          ],
          update(cache: ApolloCache, { data: _data }) {
            // Filter deleted ids out of every cached searchQuotes variant directly — this runs
            // whether or not the full QuoteDocument for a given id has ever been loaded into cache,
            // which is not guaranteed for list-only quotes (updateCachedQuotePartially silently
            // no-ops on cache misses, leaving deleted rows visible after navigation).
            updateQueries<SearchQuotesQuery>(cache, {
              query: SearchQuotesDocument,
              update: (data) => filterSearchQuotesByIds(data, quoteIds)
            })

            quoteIds.forEach((id) => {
              // we can fake the quote document as just an object with _deleted flag
              updateCachedQuotePartially(cache, id, {
                _deleted: !softDelete,
                trashed: softDelete
              })
            })

            // update trial remainingQuotes
            if (isTrial) {
              const appDocsQuery = cache.readQuery<AppDocsQuery>({ query: AppDocsDocument })

              if (appDocsQuery.appDocs.remainingQuotes >= 0) {
                cache.writeQuery<AppDocsQuery>({
                  query: AppDocsDocument,
                  data: {
                    ...appDocsQuery,
                    appDocs: {
                      ...appDocsQuery.appDocs,
                      // don't let it exceed 10 because they might be deleting the example quotes, which aren't counted towards the limit
                      remainingQuotes: Math.min(
                        appDocsQuery.appDocs.remainingQuotes + quoteIds.length,
                        TRIAL_QUOTE_LIMIT
                      )
                    }
                  }
                })
              }
            }
          }
        })
      },

      async archiveQuotes(quoteIds: string[], { unarchive }: { unarchive?: boolean } = {}) {
        return archiveQuotesMutation({
          variables: { ids: quoteIds, unarchive },
          refetchQueries: [getOperationName(SearchQuotesDocument)],
          optimisticResponse: {
            archiveQuotes: quoteIds.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null,
              reason: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            quoteIds.forEach((id) => {
              // we can fake the quote document as just an object with _deleted flag
              updateCachedQuotePartially(cache, id, { archived: !unarchive })
            })
          }
        })
      },

      async acceptAdditionalWork({
        rev,
        quoteId,
        signature,
        note
      }: {
        rev: string
        quoteId: string
        signature?: string
        note?: string
      }) {
        return acceptAdditionalWorkMutation({
          variables: {
            rev,
            signature,
            quoteId,
            note
          }
        })
      },

      async acceptQuote({ quote, quoteId, signature }: { quote: QuoteDocument; quoteId: string; signature?: string }) {
        return acceptQuoteMutation({
          variables: {
            quote,
            signature,
            quoteId
          }
        })
      },

      async unlinkDeal(quoteId: string) {
        return unlinkDealMutation({
          variables: {
            quoteId
          },
          refetchQueries: [getOperationName(UnscheduledJobsDocument)]
        })
      },

      async linkDeal(quoteId: string, dealId: string, updateJobAddress?: boolean) {
        return linkDealMutation({
          variables: {
            quoteId,
            dealId,
            updateJobAddress
          }
        })
      },

      async requestReview({ sms, content }: RequestReviewMutationVariables) {
        return requestReviewMutation({
          variables: {
            sms,
            content
          }
        })
      },

      async requestPayment(variables: RequestPaymentMutationVariables) {
        return requestPaymentMutation({
          variables
        })
      },

      async recoverInvoice({ id }: RecoverInvoiceMutationVariables) {
        // No cache work needed here: RecoveryNotice is the only caller and it immediately calls
        // quoteContext.save(restoredInvoice) afterwards. That save path (saveQuote above) detects
        // the restore transition and handles the searchQuotes cache once — avoiding an
        // evict-then-refetch-then-evict sequence that could flash an empty list view.
        return recoverInvoiceMutation({
          variables: {
            id
          }
        })
      },

      async syncQuote({ id, rev }: { id: string; rev: string }) {
        return syncQuoteMutation({
          variables: {
            id,
            rev
          }
        })
      },

      async sendQuote(
        quoteId: string,
        { emails, view }: { emails: SendQuoteMutationVariables['emails']; view: string; app?: string }
      ) {
        return sendQuoteMutation({
          context: {
            onlineOnly: true
          },
          variables: {
            id: quoteId,
            emails,
            view
          },
          // Unlike saveQuote, sending is a rare, deliberate action that visibly appends an
          // activity, so it's worth the extra query to refresh QuoteSidebar's last-activity line
          // immediately rather than waiting up to 50s for its poll. `activitiesForDoc` alone
          // doesn't cover it — Apollo matches the *operation* name, and `sidebarInfo` merely
          // selects the `activitiesForDoc` field.
          refetchQueries: [getOperationName(ActivitiesForDocDocument), QUOTE_SIDEBAR_INFO_QUERY_NAME],
          update(cache: ApolloCache, { data }) {
            if (data) {
              updateCachedQuote(cache, data.sendQuote.quote)
            }
          }
        })
      }
    }),
    []
  )
}

function makeQuoteOptimistic(quote: QuoteDocument) {
  if (!quote) {
    return null
  }

  return {
    ...quote,
    __typename: 'JSON',
    // null is important, if its undefined it will break cache for quote (undefined is not valid graphql value)
    _rev: quote._rev || null,
    number: quote.number ?? null
  }
}
